import React, { useMemo } from 'react'
import { connect } from 'react-redux'
import { partyApiSelectors } from '../../../modules/api/party'
import { walletApiSelectors } from '../../../modules/api/wallet'
import Search from '../../components/molecules/Search/Search'
import { partySearchSelectors, partySearchEvents } from '../../../modules/ui/PartySearch'
import { reject, pipe, insert, map, addIndex, flatten, filter, groupBy } from 'ramda'
import { $TSFixMe } from 'types/ts-migrate'
import PortfolioStatusLabel from 'modules/portfolios/components/PortfolioStatusLabel'
import { portfolioApiSelectors } from 'modules/api/portfolio'
import { List, AggregateListItem } from '../../components/molecules/List/List'
import { Button, Label } from '../../components'
import styles from '../../components/molecules/Search/Search.module.scss'

// Every mapStateToProps run rebuilt `resultSets` (a fresh `[]` when there's no active search),
// so `connect` saw a new reference on every dispatch and re-rendered the dropdown even when
// nothing changed (render churn on e.g. the Commission agent search). Return a shared stable
// reference for the empty case so connect can bail out.
const EMPTY_RESULT_SETS: $TSFixMe[] = []
const isResultSetsEmpty = (sets: $TSFixMe[] = []) => !sets.some((set: $TSFixMe) => Array.isArray(set) && set.length > 0)

// Mirror of Search's `toGroups`, but pairs each result set with a heading (by index).
// Empty sets are dropped *after* headings are assigned so the surviving sets keep their labels.
const toGroups = (sets: $TSFixMe[] = [], headings: React.ReactNode[] = []): $TSFixMe[] =>
  sets
    .map((results: $TSFixMe, i: number) => ({ heading: headings[i], results }))
    .filter((g: $TSFixMe) => g.results.length > 0)

const mapStateToProps = (state: any, ownProps: any): $TSFixMe => {
  const {
    name,
    value,
    tags,
    splitByTag,
    splitByAccount,
    placeholder = 'Search contacts',
    orderByTags,
    resultSetHeadings,
    limitResults,
    includePortfolioStatus = false,
    includeIdentification = false,
    showAddNewContact = false,
    onAddNewContact,
    ...otherProps
  } = ownProps

  const isLoading = partySearchSelectors.isSearching(state)
  const searchTerm = partySearchSelectors.getSearchQueryWithTags(state)(tags)
  let hasIdentification = false

  // Memoize base result sets to avoid recalculation
  let resultSets: $TSFixMe = splitByTag
    ? partyApiSelectors.partiesSearchResultsSplitByTag(state)(searchTerm, tags.split(','))
    : splitByAccount
    ? partyApiSelectors.partiesSearchResultsSplitByAccount(state)(searchTerm)
    : partyApiSelectors
        .partiesForSearchTerm(state)(searchTerm)
        .filter(resultSet => resultSet.length !== 0)

  if (limitResults) {
    resultSets = [
      pipe(
        // @ts-expect-error
        flatten(),
        filter((p: any) => (limitResults === 'person' ? p.partyType === 0 : p.partyType === 1)),
      )(resultSets),
    ]
  }

  // When headings are requested we build labelled groups below (which handles team ordering
  // itself), so skip the flat reorder here to avoid doing the work twice.
  if (orderByTags && !resultSetHeadings) {
    if (!searchTerm) {
      resultSets = []
    } else {
      const orderByTagsResult = partyApiSelectors.partiesSearchResultsByTags(state)(searchTerm, orderByTags.split(','))

      const agentsAndAgenciesIds = orderByTagsResult.map((a: any) => a.id)
      const mapIndexed = addIndex(map)

      if (orderByTagsResult.length > 0) {
        resultSets = pipe(
          insert(0, orderByTagsResult),
          mapIndexed((resultSet: any, i: any) => {
            return i > 0 ? reject((party: any) => agentsAndAgenciesIds.includes(party.id), resultSet) : resultSet
          }),
          reject((resultSet: any) => resultSet.length === 0),
        )(resultSets)
      }
    }
  }

  // Dynamically determine if we should show portfolio status (only if portfolioId exists)
  const hasPortfolioIds =
    pipe(
      flatten,
      filter((result: any) => result.portfolioId),
    )(resultSets).length > 0

  const shouldIncludePortfolioStatus = includePortfolioStatus && hasPortfolioIds

  if (shouldIncludePortfolioStatus) {
    const statusOrder = ['active', 'expiring', 'renewal', 'draft', 'expired', 'terminated', 'deleted', 'undefined']
    const getResultStatus = (result: $TSFixMe) => {
      const portfolioId = result?.portfolioId
      return portfolioId && portfolioApiSelectors.getIndependantStatusByPortfolioId(state)(portfolioId).toLowerCase()
    }

    resultSets = resultSets.map((resultSet: $TSFixMe) => {
      const consolidatedRows = resultSet.filter((r: $TSFixMe) => r.isConsolidatedBalance)
      const rest = resultSet.filter((r: $TSFixMe) => !r.isConsolidatedBalance)

      const groupedWalletsByStatus = groupBy(getResultStatus, rest)
      const sortedResultSet = statusOrder
        .map(status => groupedWalletsByStatus[status || ''])
        .filter(Boolean)
        .flat()

      return [...consolidatedRows, ...sortedResultSet].map((result: $TSFixMe) => {
        const portfolioStatusLabel = getResultStatus(result)
        const portfolioStatus = <PortfolioStatusLabel status={portfolioStatusLabel} />
        return { ...result, children: portfolioStatusLabel ? portfolioStatus : result.children ?? null }
      })
    })
  }

  // Always render consolidated balance for results that have the flag
  resultSets = resultSets.map((resultSet: $TSFixMe) => {
    return resultSet.map((result: $TSFixMe) => {
      if (result.isConsolidatedBalance) {
        const consolidatedBalance = walletApiSelectors.getConsolidatedBalance(state)(result.id)
        return {
          ...result,
          children: consolidatedBalance ? (
            <Label circular className={styles['consolidated-label']}>
              Consolidated
            </Label>
          ) : null,
        }
      }
      return result
    })
  })

  if (includeIdentification) {
    hasIdentification = true
    resultSets = resultSets.map((resultSet: $TSFixMe) => {
      return resultSet.map((result: $TSFixMe) => {
        const idOrPassportNumber = partyApiSelectors.getPersonsIdNumber(state)(result.id)
          ? partyApiSelectors.getPersonsIdNumber(state)(result.id)
          : partyApiSelectors.getPersonsPassportNumber(state)(result.id)

        return {
          ...result,
          children:
            partyApiSelectors.getPartyTypeById(state)(result.id) === 'person' ? (
              <div className="children">{idOrPassportNumber}</div>
            ) : (
              <span className="children">{partyApiSelectors.getPartyCompanyRegistration(state)(result.id)}</span>
            ),
        }
      })
    })
  }

  // Present results as labelled groups when the caller supplies headings (mirrors Search's
  // toGroups). We split the (already-decorated) results into a "team" set — parties tagged with
  // any of the orderByTags tags — and everyone else, then pair each set with its heading.
  // Left undefined when not in headings mode so the empty return stays referentially stable.
  let resultGroups
  if (resultSetHeadings && searchTerm) {
    const teamTags = (orderByTags || '').split(',').filter(Boolean)
    const teamResults = teamTags.length ? partyApiSelectors.partiesSearchResultsByTags(state)(searchTerm, teamTags) : []
    const teamIds = new Set(teamResults.map((r: $TSFixMe) => r.id))
    const restResults = flatten(resultSets).filter((r: $TSFixMe) => !teamIds.has(r.id))
    resultGroups = toGroups([teamResults, restResults], resultSetHeadings)
  }

  // Custom no-results renderer with "Create contact" action
  const renderNoResultsContent =
    showAddNewContact && typeof onAddNewContact === 'function'
      ? (noResultsMessage: any) => (
          <>
            <List>
              <AggregateListItem text={noResultsMessage} />
            </List>
            <div style={{ padding: '0 16px 16px 16px', textAlign: 'center' }}>
              <Button
                onMouseDown={(e: any) => {
                  e.preventDefault()
                  e.stopPropagation()
                  e.nativeEvent?.stopImmediatePropagation?.()
                  onAddNewContact()
                }}
                onClick={(e: any) => {
                  e.preventDefault()
                  e.stopPropagation()
                }}
                block
              >
                Create a new contact
              </Button>
            </div>
          </>
        )
      : undefined

  return {
    hasIdentification,
    name,
    isLoading,
    resultSets: isResultSetsEmpty(resultSets) ? EMPTY_RESULT_SETS : resultSets,
    resultGroups,
    placeholder,
    tags,
    value,
    renderNoResultsContent,
    ...otherProps,
  }
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const mapDispatchToProps = (dispatch, ownProps) => ({
  onSearchChange: (e: any) => {
    if (typeof ownProps.onChange === 'function') {
      ownProps.onChange(e)
    }
    if (ownProps.includePortfolioStatus) {
      dispatch(partySearchEvents.partySearchWithPortfolioStatus({ query: e.target.value, tags: ownProps.tags }))
    } else {
      dispatch(partySearchEvents.partySearchQuery({ query: e.target.value, tags: ownProps.tags }))
    }
  },

  onResultSelect: (e: any, { result }: any) => {
    if (typeof ownProps.onResultSelect === 'function') {
      ownProps.onResultSelect(result)
    }
  },

  onFocus: () => {
    if (ownProps.value && ownProps.value.length > 0) {
      dispatch(partySearchEvents.partySearchQuery({ query: ownProps.value, tags: ownProps.tags }))
    }
  },
})

export default connect(mapStateToProps, mapDispatchToProps)(Search)
